import { createContext, useContext, type ReactNode } from 'react';
import type { Permission } from '@bekebi/domain';
import type { SessionUser } from './session';

/**
 * The signed-in user, provided by the authenticated layout from the API session. Future screens
 * (shell, account menu) read email, role, Bekebi User ID and permissions from here — never from
 * local storage and never from a hard-coded user.
 */
const CurrentUserContext = createContext<SessionUser | null>(null);

export function CurrentUserProvider({
  user,
  children,
}: {
  user: SessionUser;
  children: ReactNode;
}) {
  return <CurrentUserContext.Provider value={user}>{children}</CurrentUserContext.Provider>;
}

/** Only valid inside the authenticated area. */
export function useCurrentUser(): SessionUser {
  const user = useContext(CurrentUserContext);
  if (!user) throw new Error('useCurrentUser must be used inside the authenticated area');
  return user;
}

/**
 * Whether the session lists a permission. For navigation and presentation only: the API checks
 * every request itself and refuses anything the role does not allow.
 */
export function useHasPermission(permission: Permission): boolean {
  return useCurrentUser().permissions.includes(permission);
}
